import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccionesDialog, EstadosContrato } from '@models/base/identificadores.model';
import { ContratoDetailDto } from '@models/contrato/contratoDetailDto.model';
import { ContratoDto } from '@models/contrato/contratoDto.model';
import { ResindirContratoRequest } from '@models/contrato/resindirContratoRequest.model';
import { ContratosService } from '@services/contratos/contratos.service';
import { PersonasService } from '@services/personas/personas.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { DialogImputacionPropietarioComponent } from '@views/propietarios/dialog-imputacion-propietario/dialog-imputacion-propietario.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { DialogGenerarResumenComponent } from '../form-editar-contrato/dialog-generar-resumen/dialog-generar-resumen.component';
import { DialogNuevaLiquidacionComponent } from '../form-editar-contrato/dialog-nueva-liquidacion/dialog-nueva-liquidacion.component';
import { DialogRescisionContratoComponent } from '../form-editar-contrato/dialog-rescision-contrato/dialog-rescision-contrato.component';
import { DialogNuevoReciboComponent } from '../dialog-nuevo-recibo/dialog-nuevo-recibo/dialog-nuevo-recibo.component';
import { HistorialCambioDialogComponent } from '@views/historial-cambio/historial-cambio-dialog.component';
import { DialogFechaLiquidacionComponent, DialogFechaLiquidacionResult } from '@views/comprobantes/dialog-fecha-liquidacion/dialog-fecha-liquidacion.component';

@Component({
  selector: 'app-acciones-contrato',
  templateUrl: './acciones-contrato.component.html',
  styleUrls: ['./acciones-contrato.component.css']
})
export class AccionesContratoComponent implements OnInit {
  @Input() contratoDetailDto!: ContratoDetailDto
  @Input() idContrato!: number
  estadosContrato!: typeof EstadosContrato;

  /**
   * Un contrato terminado suele dejar deuda del inquilino sin cobrar. Para no
   * dejar esa plata trabada, en Finalizado y Rescindido se habilitan el recibo y
   * la liquidación, pero SÓLO si efectivamente hay algo pendiente.
   */
  tieneDeudaPendiente = false;
  consultandoDeuda = false;

  constructor(
    private dialog: MatDialog,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private contratosService: ContratosService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.estadosContrato = EstadosContrato

    if (isFalsy(this.contratoDetailDto) && !isFalsy(this.idContrato))
      this.getById(this.idContrato)
    else
      this.verificarDeudaPendiente()
  }

  /** Estados en los que el contrato ya terminó pero puede quedar deuda por cobrar */
  private get contratoTerminado(): boolean {
    const idEstado = this.contratoDetailDto?.estadoContrato?.id

    return idEstado === EstadosContrato.Finalizado || idEstado === EstadosContrato.Rescindido
  }

  /**
   * Sólo se consulta en contratos terminados: en los activos no hace falta,
   * porque las acciones ya están habilitadas.
   */
  private verificarDeudaPendiente() {
    if (!this.contratoTerminado) {
      this.tieneDeudaPendiente = false
      return
    }

    this.consultandoDeuda = true

    this.contratosService.getDeudaPendiente(this.contratoDetailDto.id).subscribe(
      data => {
        this.tieneDeudaPendiente = !isFalsy(data) && data.tieneDeuda
        this.consultandoDeuda = false
      },
      error => {
        // Ante la duda no se habilita nada: es preferible que el usuario avise
        // a que se emita un comprobante sobre un contrato cerrado y saldado.
        this.tieneDeudaPendiente = false
        this.consultandoDeuda = false
      }
    )
  }

  /**
   * Habilita cobrar y liquidar: activo siempre, o terminado con deuda pendiente.
   * Se usa desde el template para el recibo y la liquidación.
   */
  puedeCobrarYLiquidar(): boolean {
    if (isFalsy(this.contratoDetailDto) || isFalsy(this.contratoDetailDto.estadoContrato))
      return false

    if (this.contratoDetailDto.estadoContrato.id === EstadosContrato.Activo)
      return true

    return this.contratoTerminado && this.tieneDeudaPendiente
  }

  getById(id: number) {
    this.spinner.show()
    this.contratosService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide()
          this.contratoDetailDto = data
          this.verificarDeudaPendiente()
        },
        error => {
          this.spinner.hide()
          this.snackBar.showError(error, "Error");
        }
      )
  }

  openDialogGenerarResumen(id: number) {
    this.spinner.show()
    this.contratosService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide()
          const dialogConfig = new MatDialogConfig();
          dialogConfig.width = "400px"
          dialogConfig.autoFocus = false
          dialogConfig.disableClose = true

          dialogConfig.data = { event: AccionesDialog.Agregar, contrato: data }
          const dialogRef = this.dialog.open(DialogGenerarResumenComponent,
            dialogConfig);

          dialogRef.afterClosed().subscribe(result => {
          });
        },
        error => {
          this.spinner.hide()
          this.snackBar.showError(error, "Error");
        }
      )
  }


  // En tu componente
  openDialogFechaLiquidacion(id: number): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';
    dialogConfig.data = {
      mensaje: 'Seleccione la fecha de cierre para generar la liquidación del propietario'
    };

    const dialogRef = this.dialog.open(DialogFechaLiquidacionComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result: DialogFechaLiquidacionResult) => {
      if (result) {
        // Enviar al backend
        this.openDialogNuevaLiquidacion(id, result.fechaCierre);
      }
    });
  }

  openDialogNuevaLiquidacion(id: number, fechaCierre: string) {
    this.spinner.show()
    this.contratosService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide()
          const dialogConfig = new MatDialogConfig();
          dialogConfig.width = "1080px"
          dialogConfig.data = { data: data, fechaCierre: fechaCierre }
          dialogConfig.autoFocus = false
          dialogConfig.disableClose = true

          const dialogRef = this.dialog.open(DialogNuevaLiquidacionComponent,
            dialogConfig);
          dialogRef.afterClosed().subscribe(result => {

          });
        },
        error => {
          this.spinner.hide()
          this.snackBar.showError(error, "Error");
        }
      )
  }


  openDialogRescisionContrato(id: number) {
    this.spinner.show()
    this.contratosService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide()
          const dialogConfig = new MatDialogConfig();
          dialogConfig.autoFocus = false
          dialogConfig.disableClose = false
          dialogConfig.data = data

          const dialogRef = this.dialog.open(DialogRescisionContratoComponent,
            dialogConfig);

          dialogRef.afterClosed().subscribe(result => {
            if (result && result.event == AccionesDialog.Aceptar) {
              this.router.navigate(["/contratos"])
            }
          });
        },
        error => {
          this.spinner.hide()
          this.snackBar.showError(error, "Error");
        }
      )
  }

  openDialogImputacionPropietario(contratoDetailDto: ContratoDetailDto) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false
    dialogConfig.disableClose = false
    dialogConfig.width = "70%"
    dialogConfig.data = contratoDetailDto

    const dialogRef = this.dialog.open(DialogImputacionPropietarioComponent,
      dialogConfig);

    dialogRef.afterClosed().subscribe(result => {

    });
  }


  rescisionContrato(resindirContratoRequest: ResindirContratoRequest) {
    this.spinner.show()
    this.contratosService.rescisionContrato(resindirContratoRequest)
      .subscribe(
        data => {
          this.spinner.hide()
          this.snackBar.showSuccess("Contrato resindido de manera correcta.", "Exito");
        },
        error => {
          this.spinner.hide()
          this.snackBar.showError(error, "Error");
        }
      )
  }


  openDialogNuevoRecibo(id: number) {
    this.spinner.show()
    this.contratosService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide()
          const dialogConfig = new MatDialogConfig();
          dialogConfig.width = "100%"
          dialogConfig.data = { event: AccionesDialog.Agregar, contrato: data }
          dialogConfig.autoFocus = false
          dialogConfig.disableClose = true

          const dialogRef = this.dialog.open(DialogNuevoReciboComponent,
            dialogConfig);

          dialogRef.afterClosed().subscribe(result => {
          });
        },
        error => {
          this.spinner.hide()
          this.snackBar.showError(error, "Error");
        }
      )
  }

  abrirHistorialCambios(id: number): void {
    const dialogRef = this.dialog.open(HistorialCambioDialogComponent, {
      width: '90%',
      data: {
        idEntidad: id,
        nombreEntidad: 'Contratos'  // Debe coincidir EXACTAMENTE con el nombre de la tabla
      },
      panelClass: 'historial-dialog-panel',
      disableClose: false
    });

    // Opcional: hacer algo cuando se cierra el dialog
    dialogRef.afterClosed().subscribe(result => {
      // Acción después de cerrar
    });
  }

  isFalsy(valor: any) {
    return isFalsy(valor)
  }

  trackByItems(index: number, item: any): any { return item; }

}
